import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ReactElement } from "react";
import { ExpertModeTheme } from "../../theme/expert-mode"
import imageTrendUp from "../../../public/img/trendUp.svg"
import imageTrendDown from "../../../public/img/trendDown.svg"

const Root = styled.div(
    css`
        flex: 1 1 0px;
        width: 0;
    `)

const Card = styled.div<{ expertTheme: ExpertModeTheme }>(
    props => css`
        border: 1px solid ${props.expertTheme.colors.subtle};
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    `)

const Title = styled.div(
    css`
        font-family: 'inter-regular';
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        line-height: 32px;
    `)

const MainValue = styled.div(
    css`
        font-family: 'inter-regular';
        font-style: normal;
        font-weight: 700;
        font-size: 24px;
        line-height: 20px;
    `)

const Chart = styled.div(css``)

const StepSelector = styled.div<{ expertTheme: ExpertModeTheme }>(
    props => css`
        background-color: ${props.expertTheme.colors.mediumSubtle};
        display: flex;
        justify-content: space-around;
        gap: 10px;
        padding: 4px;
        border-radius: 8px;
    `)

const Step = styled.div<{ expertTheme: ExpertModeTheme, isSelected: boolean }>(
    props => css`
        border-radius: 8px;
        height: 44px;
        flex: 1 1 0px;
        width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: inter-regular;
        font-style: normal;
        font-size: 16px;
        line-height: 24px;
    `,
    props => props.isSelected ?
        css`
            background-color: ${props.expertTheme.colors.highlight};
            color: ${props.expertTheme.colors.text};
            font-weight: 600;
        `:
        css`
            color: ${props.expertTheme.colors.textVeryLight};
            font-weight: 400;
        `)

const DetailsSection = styled.div(
    css`
        display: flex;
        flex-direction: column;
        gap: 12px;
    `)

const Detail = styled.div<{ expertTheme: ExpertModeTheme }>(
    props => css`
        border-radius: 8px;
        border: 1px solid ${props.expertTheme.colors.primaryLight};
        padding: 8px;
    `)

const ImgTrend = styled.img(
    css`
        vertical-align: text-bottom;
        margin-left: 5px;
    `)

const DetailLabel = styled.div(
    css`
        font-family: inter-regular;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
    `)

const DetailValue = styled.span<{ expertTheme: ExpertModeTheme }>(
    props => css`
        font-family: inter-regular;
        font-style: normal;
        font-weight: 700;
        font-size: 18px;
        line-height: 34px;
        color: 1px solid ${props.expertTheme.colors.text};
    `)

type Props<Id = unknown> = {
    expertTheme: ExpertModeTheme,
    title: string,
    mainValue: string,
    chart: ReactElement,
    steps: Array<{ id: Id, label: string }>,
    selectedStep: Id,
    onSelectStep: (id: Id) => void;
    details: Array<{
        label: string,
        value: string,
        trend?: "up" | "down" | null
    }>
}

const StatCard = <Id,>({ mainValue, title, expertTheme, chart, steps, selectedStep, details }: Props<Id>) => {
    return (
        <Root>
            <Title>
                {title}
            </Title>
            <Card {...{ expertTheme }}>
                <MainValue>
                    {mainValue}
                </MainValue>
                <Chart {...{ expertTheme }}>
                    {chart}
                </Chart>
                <StepSelector {...{ expertTheme }}>
                    {steps.map(step => {
                        const isSelected = step.id === selectedStep;

                        return (
                            <Step {...{ expertTheme, isSelected }}>
                                {step.label}
                            </Step>)
                    })}
                </StepSelector>
                <DetailsSection >
                    {details.map(detail =>
                        <Detail {...{ expertTheme }}>
                            <DetailValue  {...{ expertTheme }}>
                                {detail.value}
                            </DetailValue>
                            {detail.trend &&
                                <ImgTrend src={
                                    {
                                        up: imageTrendUp.src,
                                        down: imageTrendDown.src
                                    }
                                    [detail.trend]
                                } />}
                            <DetailLabel  {...{ expertTheme }}>
                                {detail.label}
                            </DetailLabel>
                        </Detail>
                    )}
                </DetailsSection>
            </Card>
        </Root>
    )
}

export default StatCard